import React from "react";
import {DragDropContext, Draggable, Droppable, DraggableProvided, DraggableStateSnapshot} from "@hello-pangea/dnd";
import {createPortal} from "react-dom";
import {Box, Button, Checkbox, Menu, NumberInput, Radio, Switch, Text, TextInput, Textarea} from "@mantine/core";
import {IconChevronDown, IconChevronUp, IconDotsVertical, IconGripVertical, IconPlus, IconTrash} from "@tabler/icons-react";
import {DeepPartial} from "@/utils/types/DeepPartial";
import {TWorkflow} from "../StepView";
import {IQuestionnaireStep} from "@/protocols/types/protocol";
import {IQuestionnaireStepDefinition} from "@/protocols/types/protocolDefinitions";
import {
    IFreeQAndAPair,
    IMultiChoiceQAndAPair,
    IQuestionnaireOption,
    IQuestionnaireQAndAPair,
    TQuestionnaireQuestionKind,
} from "@/protocols/types/questionnaire";
import classes from "./QuestionnaireStepView.module.css";
import cx from "clsx";

export type QuestionnaireStep = IQuestionnaireStep | IQuestionnaireStepDefinition;

export interface QuestionnaireStepViewProps {
    step: QuestionnaireStep;
    onChange: (updates: DeepPartial<QuestionnaireStep>) => void;
    workflow?: TWorkflow;
    focusMode?: boolean;
}

const createOption = (id: number): IQuestionnaireOption => ({
    id,
    text: "",
});

export const createQuestionnaireItem = (
    kind: TQuestionnaireQuestionKind,
    id: number = 0,
): IQuestionnaireQAndAPair => {
    if (kind === TQuestionnaireQuestionKind.MULTI_CHOICE) {
        return {
            id,
            kind,
            question: "",
            allow_multiple: false,
            include_other: false,
            options: [createOption(0), createOption(1)],
            selected_option_ids: [],
        } satisfies IMultiChoiceQAndAPair;
    }

    return {
        id,
        kind,
        question: "",
        answer_text: "",
    } satisfies IFreeQAndAPair;
};

const normalizeQuestionnaireItem = (
    item: IQuestionnaireQAndAPair,
    questionId: number,
): IQuestionnaireQAndAPair => {
    if (item.kind === TQuestionnaireQuestionKind.MULTI_CHOICE) {
        const reindexedOptions = item.options.map((option, optionId) => ({
            ...option,
            id: optionId,
        }));
        const options = normalizeOptionsForOther(reindexedOptions, item.include_other);
        const validOptionIds = new Set(options.map((option) => option.id));

        return {
            ...item,
            id: questionId,
            options,
            selected_option_ids: item.selected_option_ids.filter((optionId) => validOptionIds.has(optionId)),
        } satisfies IMultiChoiceQAndAPair;
    }

    return {
        ...item,
        id: questionId,
    } satisfies IFreeQAndAPair;
};

export const normalizeQuestionnaireItems = (
    items: IQuestionnaireQAndAPair[],
): IQuestionnaireQAndAPair[] => items.map((item, index) => normalizeQuestionnaireItem(item, index));

export const reorderQuestionnaireItems = (
    items: IQuestionnaireQAndAPair[],
    sourceIndex: number,
    destinationIndex: number,
): IQuestionnaireQAndAPair[] => {
    const reordered = [...items];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(destinationIndex, 0, moved);
    return normalizeQuestionnaireItems(reordered);
};

const normalizeOptionsForOther = (
    options: IQuestionnaireOption[],
    includeOther: boolean,
): IQuestionnaireOption[] => {
    if (options.length === 0) {
        return options;
    }

    const previousOtherIndex = options.findIndex((option) => option.text === "Other");
    const lastIndex = options.length - 1;

    return options.map((option, index) => {
        if (includeOther && index === lastIndex) {
            return {...option, text: "Other"};
        }

        if (index === previousOtherIndex) {
            return {...option, text: ""};
        }

        return option;
    });
};

const renderDraggableContent = (
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot,
    children: React.ReactNode,
) => {
    const content = (
        <div ref={provided.innerRef} {...provided.draggableProps}>
            {children}
        </div>
    );

    if (snapshot.isDragging) {
        return createPortal(content, document.body);
    }

    return content;
};

export const QuestionnaireStepView: React.FC<QuestionnaireStepViewProps> = ({
    step,
    onChange,
    workflow = "session",
    focusMode = false,
}) => {
    const isPlanningMode = workflow === "planning";
    const isReviewMode = workflow === "review";
    const canAnswer = workflow === "session" || workflow === "review_edit";
    const items = normalizeQuestionnaireItems(step.items || []);
    const isQuestionPagerMode = focusMode && !isPlanningMode;
    const [activeQuestionIndex, setActiveQuestionIndex] = React.useState(0);

    React.useEffect(() => {
        if (items.length === 0) {
            setActiveQuestionIndex(0);
            return;
        }
        setActiveQuestionIndex((current) => Math.min(current, items.length - 1));
    }, [items.length]);

    const commitItems = (updatedItems: IQuestionnaireQAndAPair[]) => {
        onChange({items: normalizeQuestionnaireItems(updatedItems)});
    };

    const updateItem = (itemId: number, updater: (item: IQuestionnaireQAndAPair) => IQuestionnaireQAndAPair) => {
        commitItems(items.map((item) => item.id === itemId ? updater(item) : item));
    };

    const deleteItem = (itemId: number) => {
        commitItems(items.filter((item) => item.id !== itemId));
    };

    const addItem = (kind: TQuestionnaireQuestionKind) => {
        commitItems([...items, createQuestionnaireItem(kind, items.length)]);
    };

    const updateOptionCount = (item: IMultiChoiceQAndAPair, rawValue: string) => {
        const parsedValue = parseInt(rawValue, 10);
        if (Number.isNaN(parsedValue) || parsedValue < 1) {
            return;
        }

        const nextOptions = [...item.options];
        while (nextOptions.length < parsedValue) {
            nextOptions.push(createOption(nextOptions.length));
        }
        const reindexedOptions = nextOptions.slice(0, parsedValue).map((option, index) => ({
            ...option,
            id: index,
        }));
        const trimmedOptions = normalizeOptionsForOther(reindexedOptions, item.include_other);
        const validOptionIds = new Set(trimmedOptions.map((option) => option.id));
        const nextSelectedOptionIds = item.selected_option_ids.filter((id) => validOptionIds.has(id));
        const previousOtherOptionId = item.include_other ? item.options.length - 1 : null;
        const hasMovedOtherSlot =
            previousOtherOptionId !== null && previousOtherOptionId !== trimmedOptions.length - 1;

        updateItem(item.id, () => ({
            ...item,
            options: trimmedOptions,
            selected_option_ids: item.allow_multiple ? nextSelectedOptionIds : nextSelectedOptionIds.slice(0, 1),
            other_text: hasMovedOtherSlot ? undefined : item.other_text,
        }));
    };

    const toggleMultiChoiceOption = (item: IMultiChoiceQAndAPair, optionId: number) => {
        if (!canAnswer) {
            return;
        }

        const nextSelectedOptionIds = item.allow_multiple
            ? item.selected_option_ids.includes(optionId)
                ? item.selected_option_ids.filter((id) => id !== optionId)
                : [...item.selected_option_ids, optionId]
            : [optionId];
        const otherOptionId = item.include_other && item.options.length > 0
            ? item.options.length - 1
            : null;
        const shouldClearOtherText =
            otherOptionId !== null && !nextSelectedOptionIds.includes(otherOptionId);

        updateItem(item.id, () => ({
            ...item,
            selected_option_ids: nextSelectedOptionIds,
            other_text: shouldClearOtherText ? undefined : item.other_text,
        }));
    };

    const isOtherOption = (item: IMultiChoiceQAndAPair, optionId: number) => {
        return item.include_other && optionId === item.options.length - 1;
    };

    const isOtherSelected = (item: IMultiChoiceQAndAPair) => {
        if (!item.include_other || item.options.length === 0) {
            return false;
        }
        const otherOptionId = item.options.length - 1;
        return item.selected_option_ids.includes(otherOptionId);
    };

    const renderOtherTextInput = (item: IMultiChoiceQAndAPair, disabled: boolean) => {
        if (!item.include_other) {
            return null;
        }

        return (
            <Textarea
                data-testid={`questionnaire-other-text-${item.id}`}
                label="Other"
                value={item.other_text || ""}
                onChange={(event) => updateItem(item.id, () => ({
                    ...item,
                    other_text: event.currentTarget.value,
                }))}
                disabled={disabled}
                autosize
                minRows={2}
            />
        );
    };

    const renderPlanningAnswerPreview = (item: IQuestionnaireQAndAPair) => {
        if (item.kind === TQuestionnaireQuestionKind.OPEN_ENDED) {
            return (
                <Textarea
                    data-testid={`questionnaire-answer-${item.id}`}
                    label="Response"
                    placeholder="Answer entered during session"
                    disabled={true}
                    autosize
                    minRows={2}
                />
            );
        }

        return (
            <Box className={classes.answerBox}>
                <Text size="xs" c="dimmed">Response</Text>
                <Box className={classes.optionsEditorGrid}>
                    {item.options.map((option) => (
                        <Box key={option.id} className={classes.optionEditorItem}>
                            {item.allow_multiple ? <Checkbox checked={false} disabled={true}/> : <Radio checked={false} readOnly={true} disabled={true}/>}
                            <TextInput
                                size="xs"
                                className={classes.optionEditorInput}
                                data-testid={`questionnaire-option-text-${item.id}-${option.id}`}
                                value={isOtherOption(item, option.id) ? "Other" : option.text}
                                disabled={isOtherOption(item, option.id)}
                                onChange={(event) => updateItem(item.id, (currentItem) => ({
                                    ...(currentItem as IMultiChoiceQAndAPair),
                                    options: (currentItem as IMultiChoiceQAndAPair).options.map((currentOption) =>
                                        currentOption.id === option.id
                                            ? {...currentOption, text: event.currentTarget.value}
                                            : currentOption
                                    ),
                                }))}
                            />
                        </Box>
                    ))}
                </Box>
                {renderOtherTextInput(item, true)}
            </Box>
        );
    };

    const renderAnswerArea = (item: IQuestionnaireQAndAPair) => {
        if (isPlanningMode) {
            return renderPlanningAnswerPreview(item);
        }

        if (item.kind === TQuestionnaireQuestionKind.OPEN_ENDED) {
            return (
                <Textarea
                    data-testid={`questionnaire-answer-${item.id}`}
                    label="Response"
                    value={item.answer_text || ""}
                    onChange={(event) => updateItem(item.id, () => ({
                        ...item,
                        answer_text: event.currentTarget.value,
                    }))}
                    disabled={!canAnswer || isReviewMode}
                    autosize
                    minRows={2}
                />
            );
        }

        return (
            <Box className={classes.answerBox}>
                <Text size="xs" c="dimmed">Response</Text>
                <Box className={classes.optionsList}>
                    {item.options.map((option) => {
                        const checked = item.selected_option_ids.includes(option.id);
                        const disabled = !canAnswer || isReviewMode;
                        const control = item.allow_multiple
                            ? <Checkbox data-testid={`questionnaire-option-control-${item.id}-${option.id}`} checked={checked} onChange={() => toggleMultiChoiceOption(item, option.id)} disabled={disabled}/>
                            : <Radio data-testid={`questionnaire-option-control-${item.id}-${option.id}`} checked={checked} onChange={() => toggleMultiChoiceOption(item, option.id)} disabled={disabled}/>;

                        return (
                            <Box
                                key={option.id}
                                className={classes.optionRow}
                            >
                                <Box onClick={(event) => event.stopPropagation()}>
                                    {control}
                                </Box>
                                <Text>{option.text || `Option ${item.options.findIndex((candidate) => candidate.id === option.id) + 1}`}</Text>
                            </Box>
                        );
                    })}
                </Box>
                {renderOtherTextInput(item, !canAnswer || isReviewMode || !isOtherSelected(item))}
            </Box>
        );
    };

    const renderQuestionCard = (item: IQuestionnaireQAndAPair, index: number, dragHandleProps?: Record<string, unknown>, isDragging = false) => {
        const multiChoiceItem = item.kind === TQuestionnaireQuestionKind.MULTI_CHOICE ? item : undefined;

        return (
            <Box className={cx(classes.card, {[classes.cardDragging]: isDragging})} data-testid={`questionnaire-question-${index}`}>
                <Box className={classes.cardHeader}>
                    {isPlanningMode ? (
                        <Box className={classes.dragHandle} {...dragHandleProps}>
                            <IconGripVertical size="1rem"/>
                        </Box>
                    ) : <Box/>}
                    <Box>
                        <Text size="xs" c="dimmed" mb={4}>Question {index + 1}</Text>
                        {isPlanningMode ? (
                            <TextInput
                                data-testid={`questionnaire-question-input-${index}`}
                                value={item.question}
                                placeholder="Enter question"
                                onChange={(event) => updateItem(item.id, () => ({
                                    ...item,
                                    question: event.currentTarget.value,
                                }))}
                            />
                        ) : (
                            <Text className={classes.questionText}>{item.question || `Question ${index + 1}`}</Text>
                        )}
                    </Box>
                    <Box className={classes.questionActions}>
                        {isPlanningMode && (
                            <Button
                                variant="subtle"
                                color="red"
                                size="compact-xs"
                                leftSection={<IconTrash size={14}/>}
                                onClick={() => deleteItem(item.id)}
                                data-testid={`questionnaire-delete-${index}`}
                            >
                                Delete
                            </Button>
                        )}
                    </Box>
                </Box>

                {multiChoiceItem && isPlanningMode && (
                    <Box className={classes.optionConfigRow}>
                        <NumberInput
                            data-testid={`questionnaire-option-count-${index}`}
                            label="Number of options"
                            value={multiChoiceItem.options.length}
                            min={1}
                            allowNegative={false}
                            hideControls
                            onBlur={(event) => updateOptionCount(multiChoiceItem, event.currentTarget.value)}
                        />
                        <Switch
                            data-testid={`questionnaire-allow-multiple-${index}`}
                            checked={multiChoiceItem.allow_multiple}
                            label="Allow multiple selections"
                            classNames={{
                                labelWrapper: classes.allowMultipleSwitchLabelWrapper,
                                label: classes.allowMultipleSwitchLabel,
                                track: classes.allowMultipleSwitchTrack,
                                thumb: classes.allowMultipleSwitchThumb,
                            }}
                            onChange={(event) => updateItem(item.id, () => ({
                                ...multiChoiceItem,
                                allow_multiple: event.currentTarget.checked,
                                selected_option_ids: event.currentTarget.checked
                                    ? multiChoiceItem.selected_option_ids
                                    : multiChoiceItem.selected_option_ids.slice(0, 1),
                            }))}
                        />
                        <Switch
                            data-testid={`questionnaire-include-other-${index}`}
                            checked={multiChoiceItem.include_other}
                            label="Include other"
                            classNames={{
                                labelWrapper: classes.allowMultipleSwitchLabelWrapper,
                                label: classes.allowMultipleSwitchLabel,
                                track: classes.allowMultipleSwitchTrack,
                                thumb: classes.allowMultipleSwitchThumb,
                            }}
                            onChange={(event) => updateItem(item.id, () => ({
                                ...multiChoiceItem,
                                include_other: event.currentTarget.checked,
                                options: normalizeOptionsForOther(
                                    multiChoiceItem.options,
                                    event.currentTarget.checked,
                                ),
                                other_text: event.currentTarget.checked ? (multiChoiceItem.other_text || "") : undefined,
                            }))}
                        />
                    </Box>
                )}

                {renderAnswerArea(item)}
            </Box>
        );
    };

    const renderPlanningList = () => {
        if (items.length === 0) {
            return (
                <Box className={classes.emptyState} data-testid="questionnaire-empty-state">
                    <Text size="sm" c="dimmed">No questions yet. Use Add Question to create the questionnaire.</Text>
                </Box>
            );
        }

        return (
            <DragDropContext
                onDragEnd={({destination, source}) => {
                    if (!destination || destination.index === source.index) {
                        return;
                    }
                    commitItems(reorderQuestionnaireItems(items, source.index, destination.index));
                }}
            >
                <Droppable droppableId={`questionnaire-${step.id}`} direction="vertical">
                    {(provided) => (
                        <Box className={classes.list} ref={provided.innerRef} {...provided.droppableProps}>
                            {items.map((item, index) => (
                                <Draggable key={String(item.id)} draggableId={String(item.id)} index={index}>
                                    {(draggableProvided, snapshot) => renderDraggableContent(
                                        draggableProvided,
                                        snapshot,
                                        renderQuestionCard(item, index, draggableProvided.dragHandleProps ?? undefined, snapshot.isDragging),
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </DragDropContext>
        );
    };

    const renderFocusModeQuestionPager = () => {
        if (items.length === 0) {
            return (
                <Box className={classes.emptyState}>
                    <Text size="sm" c="dimmed">No questions configured for this questionnaire.</Text>
                </Box>
            );
        }

        return (
            <Box className={classes.focusPager} data-testid="questionnaire-focus-pager">
                <Box className={classes.focusPagerHeader}>
                    <Box className={classes.focusPagerControls}>
                        <Button
                            variant="default"
                            size="compact-sm"
                            disabled={activeQuestionIndex === 0}
                            onClick={() => setActiveQuestionIndex((index) => Math.max(0, index - 1))}
                            px={8}
                            data-testid="questionnaire-focus-prev"
                            aria-label="Previous question"
                        >
                            <IconChevronUp size={14}/>
                        </Button>
                        <Text size="sm" fw={500} data-testid="questionnaire-focus-counter">
                            {`${activeQuestionIndex + 1}/${items.length}`}
                        </Text>
                        <Button
                            variant="default"
                            size="compact-sm"
                            disabled={activeQuestionIndex >= items.length - 1}
                            onClick={() => setActiveQuestionIndex((index) => Math.min(items.length - 1, index + 1))}
                            px={8}
                            data-testid="questionnaire-focus-next"
                            aria-label="Next question"
                        >
                            <IconChevronDown size={14}/>
                        </Button>
                    </Box>
                </Box>
                {renderQuestionCard(items[activeQuestionIndex], activeQuestionIndex)}
            </Box>
        );
    };

    return (
        <Box className={cx(classes.root, {[classes.focusMode]: isQuestionPagerMode})}>
            {isPlanningMode && (
                <Box className={classes.toolbar}>
                    <Menu withinPortal={false}>
                        <Menu.Target>
                            <Button leftSection={<IconPlus size={14}/>} data-testid="questionnaire-add-question-button">
                                Add Question
                            </Button>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Item
                                leftSection={<IconDotsVertical size={14}/>}
                                onClick={() => addItem(TQuestionnaireQuestionKind.MULTI_CHOICE)}
                            >
                                Multiple Choice
                            </Menu.Item>
                            <Menu.Item
                                leftSection={<IconDotsVertical size={14}/>}
                                onClick={() => addItem(TQuestionnaireQuestionKind.OPEN_ENDED)}
                            >
                                Open Ended
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                </Box>
            )}

            {isPlanningMode ? renderPlanningList() : (
                isQuestionPagerMode ? renderFocusModeQuestionPager() : (
                items.length === 0 ? (
                    <Box className={classes.emptyState}>
                        <Text size="sm" c="dimmed">No questions configured for this questionnaire.</Text>
                    </Box>
                ) : (
                    <Box className={classes.list}>
                        {items.map((item, index) => renderQuestionCard(item, index))}
                    </Box>
                )
                )
            )}
        </Box>
    );
};



